import {GridColDef} from "@mui/x-data-grid-pro";
import {getGridSingleSelectOperators} from "@mui/x-data-grid-pro";
import type {GetCohortSettingsResponse} from "@/app2/shared/data/gen";
import {useTranslation} from "react-i18next";
import {formatDateForDisplay} from "@/app2/shared/utils/dateManipulation";
import {ENROLLMENT_TYPE_FILTER_VALUES} from "@/app2/shared/constants/enrollmentTypes";

/** Server filter tokens with i18n keys under `user_enrollment_type_*` */
const TEAM_PROGRESS_ENROLLMENT_TYPE_VALUES = ENROLLMENT_TYPE_FILTER_VALUES;

/**
 * Shared column definitions for Team Progress data grids
 * Ensures consistency across all grids
 */
export function useTeamProgressColumns(organization?: GetCohortSettingsResponse) {
	const {t} = useTranslation();
	const selectOperatorIs = getGridSingleSelectOperators().filter(
		op => op.value === "is"
	);

	// Common column definitions
	const columns = {
		firstname: {
			field: "firstname",
			headerName: "First Name",
			flex: 1,
			type: "string"
		} as GridColDef,

		lastname: {
			field: "lastname",
			headerName: "Last Name",
			flex: 1,
			type: "string"
		} as GridColDef,

		email: {
			field: "email",
			headerName: "Email",
			flex: 1,
			type: "string"
		} as GridColDef,

		phone: {
			field: "phone",
			headerName: "Phone",
			flex: 1,
			type: "string"
		} as GridColDef,

		supervisor: {
			field: "supervisor",
			headerName: "Supervisor",
			flex: 1,
			type: "string"
		} as GridColDef,

		division: {
			field: "division",
			headerName: "Division",
			flex: 1,
			type: "string"
		} as GridColDef,

		jobCategory: {
			field: "jobCategory",
			headerName: "Job Category",
			flex: 1,
			type: "string"
		} as GridColDef,

		jobTitle: {
			field: "jobTitle",
			headerName: "Job Title",
			flex: 1,
			type: "string"
		} as GridColDef,

		employeeCode: {
			field: "employeeCode",
			headerName: "Employee Code",
			flex: 1,
			type: "string"
		} as GridColDef,

		status: {
			field: "status",
			headerName: "Status",
			flex: 1,
			type: "string",
			filterOperators: selectOperatorIs,
			valueOptions: ["active", "archived"]
		} as GridColDef,

		enrollmentType: {
			field: "enrollmentType",
			headerName: t("common_enrollment_type", {defaultValue: "Enrollment Type"}),
			flex: 1,
			type: "singleSelect",
			filterOperators: selectOperatorIs,
			valueOptions: TEAM_PROGRESS_ENROLLMENT_TYPE_VALUES.map((value) => ({
				value,
				label: t(`user_enrollment_type_${value}` as any, {defaultValue: value})
			})),
			valueGetter: (params) =>
				params.value
					? t(`user_enrollment_type_${params.value}` as any, params.value as string)
					: ""
		} as GridColDef,

		streak: {
			field: "streak",
			headerName: "Streak",
			flex: 1,
			type: "number"
		} as GridColDef,

		customFieldValue1: {
			field: "customFieldValue1",
			headerName: organization?.customFieldName1 ?? "Custom Field 1",
			flex: 1,
			type: "string"
		} as GridColDef,

		customFieldValue2: {
			field: "customFieldValue2",
			headerName: organization?.customFieldName2 ?? "Custom Field 2",
			flex: 1,
			type: "string"
		} as GridColDef,

		customFieldValue3: {
			field: "customFieldValue3",
			headerName: organization?.customFieldName3 ?? "Custom Field 3",
			flex: 1,
			type: "string"
		} as GridColDef
	};

	return columns;
}

/**
 * Format duration in seconds to readable format (HH:MM:SS or MM:SS)
 */
export function formatDuration(seconds: number | null | undefined): string {
	if (!seconds) return "00:00";

	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const secs = seconds % 60;

	if (hours > 0) {
		return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	}

	return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Format date to display format
 */
export function formatDate(date: string | null | undefined): string | null {
	if (!date) return null;
	return formatDateForDisplay(date);
}
